import { isoDateString } from '@clockwise/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { type AuditFilters, AuditLogView } from '@/components/custom/audit/audit-log-view';
import { NoAccess } from '@/components/custom/no-access';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { dateRangePresetSchema } from '@/lib/date-range-search';
import { useMe } from '@/lib/me';

const search = z.object({
  entityType: z.string().optional(),
  actorId: z.string().uuid().optional(),
  preset: dateRangePresetSchema.optional(),
  from: isoDateString().optional(),
  to: isoDateString().optional(),
  page: z.coerce.number().int().min(0).optional(),
});
type AuditSearch = z.infer<typeof search>;

export const Route = createFileRoute('/_authenticated/audit')({
  validateSearch: search,
  component: AuditPage,
});

function AuditPage() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { permissions, loading } = useMe();

  if (!loading && !permissions.has('audit.view')) {
    return <NoAccess permission="audit.view" resource="the audit log" />;
  }

  // Default range = no date filter (all time): a 'custom' preset with no
  // dates resolves to null, so the query omits from/to. Picking a preset or
  // dates narrows it.
  const filters: AuditFilters = {
    entityType: s.entityType,
    actorId: s.actorId,
    range: { preset: s.preset ?? 'custom', from: s.from, to: s.to },
    page: s.page ?? 0,
  };

  const onChange = (next: Partial<AuditFilters>) =>
    navigate({
      replace: true,
      search: (prev: AuditSearch): AuditSearch => {
        const merged: AuditSearch = { ...prev };
        if ('entityType' in next) merged.entityType = next.entityType || undefined;
        if ('actorId' in next) merged.actorId = next.actorId || undefined;
        if ('page' in next) merged.page = next.page && next.page > 0 ? next.page : undefined;
        if (next.range) {
          merged.preset = next.range.preset;
          merged.from = next.range.from;
          merged.to = next.range.to;
        }
        return merged;
      },
    });

  return (
    <PageShell>
      <PageHeader
        title="Audit log"
        subtitle="Every change across the platform — filter by time, object type, or employee."
      />
      <AuditLogView filters={filters} onChange={onChange} />
    </PageShell>
  );
}
